import { MAX_SCHEDULING_HOURS } from 'citymd/constants/appointment';
import { DEFAULT_TIMEZONE } from 'citymd/constants/date';
import { getHours, getMinutes } from 'date-fns';
import { DefaultTimeZone } from 'utils/appointment';
import { formatDate, getDepartmentTimezone, getRelativeDayOfTheWeek } from 'utils/date';

/**
 * Get the adjusted max hours for scheduling.
 * We only want to show full days of scheduling including today so we need to adjust for the current hour of the day
 */
const getAdjustedSchedulingMaxHours = () => {
    // Get the current hour of the day and add 1 to round up so that we don't show partial days.
    // AppointmentFactory (src/utils/appointment.ts) only accepts whole hours for defaultMaxHours & returns appointments between the current time & the current time + the defaultMaxHours.
    // So if we're even 1 minute past the hour we need to round up to the next hour.
    // Otherwise we'll show appointments after midnight on the 3rd day.
    const now = new Date();
    const currentHour = getHours(now);
    const currentMinute = getMinutes(now);
    const roundedCurrentHour = currentMinute > 0 ? currentHour + 1 : currentHour;
    const difference = MAX_SCHEDULING_HOURS - roundedCurrentHour;
    return difference > 0 ? difference : 0;
};

/**
 * @description Uses an Date object to generate a header in the specified format
 * @param {Date} date A Date object
 * @param {DateFormatName} format The format in which to parse the date
 * @param {string} timeZone If provided, appends the time zone of the department
 * @returns {string}
 */
const getRelativeDateHeader = (
    date: Date,
    format: 'appointmentDate' | 'monthAndDate' = 'monthAndDate',
    timeZone: DefaultTimeZone = DEFAULT_TIMEZONE
): string => {
    const monthsToSpellOut = [2, 3, 4, 5, 6]; // March - July
    const formattedDate = monthsToSpellOut.includes(date.getMonth())
        ? formatDate(date, format === 'appointmentDate' ? 'appointmentDateAlternativeLong' : 'shortDay')
        : formatDate(date, format === 'appointmentDate' ? 'appointmentDateAlternativeShort' : 'shorterDay');

    const dateHeader = `${getRelativeDayOfTheWeek(date, 'EEEE')}, ${formattedDate}`;
    return format === 'appointmentDate' ? `${dateHeader} ${getDepartmentTimezone(timeZone).short}` : dateHeader;
};

export { getAdjustedSchedulingMaxHours, getRelativeDateHeader };
